// ═══════════════════════════════════════════════════════════
// Formatting utilities — phone, date, currency, etc.
// ═══════════════════════════════════════════════════════════

// ── Phone ────────────────────────────────────────────────

/** Format a raw phone string as (555) 123-4567 */
export function formatPhone(raw: string | undefined | null): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return raw;
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * onInput handler for phone fields — masks as user types.
 * Usage: <input onInput={maskPhone} onChange={...} />
 */
export function maskPhone(e: React.FormEvent<HTMLInputElement>) {
  const input = e.currentTarget;
  const digits = input.value.replace(/\D/g, '').slice(0, 10);
  input.value = formatPhone(digits);
}

// ── Date ─────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
});

const DATETIME_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
  hour: 'numeric', minute: '2-digit',
});

const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric', minute: '2-digit',
});

/** "Jan 5, 2025" */
export function formatDate(value: string | Date | undefined | null): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return String(value);
    // Treat date-only strings (YYYY-MM-DD) as local noon to avoid timezone shifts
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, day] = value.split('-').map(Number);
      return DATE_FMT.format(new Date(y, m - 1, day));
    }
    return DATE_FMT.format(d);
  } catch { return String(value); }
}

/** "Jan 5, 2025 at 2:30 PM" */
export function formatDateTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return String(value);
    return DATETIME_FMT.format(d).replace(',', '').replace(/(\d{4})\s/, '$1 at ');
  } catch { return String(value); }
}

/** "2:30 PM" */
export function formatTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return String(value);
    return TIME_FMT.format(d);
  } catch { return String(value); }
}

// ── Currency ─────────────────────────────────────────────

const MONEY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2,
});

const MONEY_FMT_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/** "$1,234.56" */
export function formatMoney(amount: number | string | undefined | null): string {
  if (amount === undefined || amount === null || amount === '') return '$0.00';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(n)) return '$0.00';
  return MONEY_FMT.format(n);
}

/** "$1,235" (no cents, for estimates) */
export function formatMoneyWhole(amount: number | undefined | null): string {
  if (amount === undefined || amount === null) return '$0';
  return MONEY_FMT_WHOLE.format(amount);
}

// ── Numbers ───────────────────────────────────────────────

/** "42%" */
export function formatPercent(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}
